/**
 * Acción de REDUX que permite llevar a cabo todas las operaciones para subir archivos al storage
 */

import { UPLOAD, DOCUMENTS } from '../../../application/types';
import { toAccountingList, accountingUploadBlock } from '../../lib/accounting_refs';
import { FILES_SUBCOLLECTION, shouldOverflowToSubcollection, buildFileEntry } from '../../lib/folder_files';
import { denormalizeFileFlagsToParent } from '../../lib/denormalize_file_flags';
import { files as filesActions } from '../database/documents';

/**
 * Genera un ID único para el archivo combinando el timestamp con un sufijo
 * aleatorio de 6 caracteres en base-36. Esto elimina colisiones cuando dos
 * subidas ocurren en el mismo milisegundo (ej. carga masiva concurrente de
 * múltiples clientes). Sin el sufijo, dos subidas simultáneas apuntarían al
 * mismo doc global `files/{name}` y la última sobrescribiría a la primera.
 *
 * Ejemplo: "Archivo No - 1781023000359-k4z9rx"
 */
const generateFileId = () => {
  const ts = Date.now().toString();
  const rnd = Math.random().toString(36).slice(2, 8).padEnd(6, '0');
  return `${ts}-${rnd}`;
};

/**
 * Reintenta una función async con backoff exponencial. Útil para llamadas a
 * Storage/Firestore que pueden fallar por throttling o conectividad débil.
 * Si todos los intentos fallan, lanza el último error.
 */
const retryWithBackoff = async (fn, { maxRetries = 3, baseDelayMs = 1000, label = 'op' } = {}) => {
  let lastErr;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const code = err?.code || '';
      // Errores que NO valen la pena reintentar
      if (typeof code === 'string' && /unauthenticated|permission-denied|invalid-argument/i.test(code)) {
        throw err;
      }
      if (attempt < maxRetries) {
        const delay = baseDelayMs * Math.pow(2, attempt);
        console.warn(`[upload retry] ${label} falló intento ${attempt + 1}/${maxRetries + 1}, reintentando en ${delay}ms:`, err?.message || err);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }
  throw lastErr;
};

/**
 * Función que permite subir archivos al storage
 * @param {object} file
 * @param {object} reference  CollectionReference de la sub-colección `files/` del folder destino
 */
export const upload = ( file, reference ) => ( dispatch, state, firebase ) => {

  dispatch({ type: UPLOAD.UPLOADING });

  return new Promise( async ( resolve, reject ) => {
    // Detección temprana de offline — evita intentar y dejar archivos en limbo.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return reject(
        dispatch({
          type: UPLOAD.ERROR,
          code: 'offline',
          message: 'Sin conexión a Internet. Recupera la red y vuelve a intentar.'
        })
      );
    }

    try {

      const db = firebase().firestore();
      const name = `Archivo No - ${ generateFileId() }`;
      const ref = firebase().storage().ref().child(`${ reference.path.replace('folders', 'documents').replace('files', 'documents') }/${ name }`);
      const docfile = db.doc(`files/${ name }`);
      const docfolder = reference.doc( name );
      // El parent de la CollectionReference es el folder doc (donde vive el array files[])
      const folderDocRef = reference.parent;
      const customer = ( await db.doc(`customers/${ docfolder.path.split("/")[1] }`).get() );

      // Upload a Storage con retry exponencial (3 reintentos)
      const snapshot = await retryWithBackoff(
        () => ref.put( file ),
        { maxRetries: 3, baseDelayMs: 1000, label: `storage.put ${file.name}` }
      );
      const url = await ref.getDownloadURL();

      const uid = firebase().auth().currentUser.uid;
      const batch = db.batch();

      const log_audit = db.collection('audit').doc();
      const after = {
        folder: docfolder,
        customer: customer.data()['Razon social'],
        customerId: docfolder.path.split('/')[1], // requerido por trigger onDocumentUploaded
        date_of_upload: new Date( snapshot.metadata.timeCreated ),
        name: file.name,
        type: snapshot.metadata.contentType,
        url: url
      }

      // Entrada canónica del archivo (lo que leen ambas vistas). MISMA forma
      // en el array files[] y en la subcolección __files__.
      const fileEntry = buildFileEntry({
        name: file.name,
        url,
        type: snapshot.metadata.contentType,
        date_of_upload: new Date(snapshot.metadata.timeCreated),
        fileId: name,
      });

      batch.set(log_audit, { uid, eventType: `${ DOCUMENTS.CREATE.CREATE_DOCUMENTS }_FILES`, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
      batch.set(log_audit.collection('after').doc(), after);
      batch.set(docfile, after);
      batch.set(docfolder, { reference: docfile });

      // ¿La carpeta destino ya está cerca del límite de 1 MiB del array files[]?
      // Si sí, el archivo va a la subcolección __files__ (docs individuales,
      // capacidad ilimitada) y se marca filesOverflow. Si no, camino rápido
      // (array, 1 sola lectura al expandir). Ver lib/folder_files.js.
      let overflow = false;
      if (folderDocRef && typeof folderDocRef.get === 'function') {
        try {
          const fSnap = await folderDocRef.get();
          overflow = shouldOverflowToSubcollection(fSnap.data());
        } catch (e) {
          // Si no se puede leer, por seguridad usamos __files__ (nunca revienta
          // por tamaño); el peor caso es una lectura extra al expandir.
          overflow = true;
        }
      }
      if (folderDocRef && typeof folderDocRef.set === 'function') {
        if (overflow) {
          batch.set(folderDocRef.collection(FILES_SUBCOLLECTION).doc(name), fileEntry);
          batch.set(folderDocRef, { filesOverflow: true }, { merge: true });
        } else {
          batch.set(folderDocRef, {
            files: firebase().firestore.FieldValue.arrayUnion(fileEntry),
          }, { merge: true });
        }
      }

      // Commit con retry (Firestore puede tirar UNAVAILABLE momentáneamente)
      await retryWithBackoff(
        () => batch.commit(),
        { maxRetries: 3, baseDelayMs: 1000, label: `batch.commit ${file.name}` }
      );

      return resolve(
        dispatch({
          type: UPLOAD.UPLOADED,
          data: snapshot
        }
      ));

    } catch ( error ) {
      return reject(
        dispatch({
          type: UPLOAD.ERROR,
          code: error.code,
          message: error.message
        })
      )
    }
  });

}

/**
 * Función que permite subir el archivo de contabilización al storage
 * @param {object} file 
 * @param {object} reference 
 */
export const accounting = ( file, reference ) => ( dispatch, state, firebase ) => {

  dispatch({ type: UPLOAD.UPLOADING });

  return new Promise( async ( resolve, reject ) => {
    try {

      const db = firebase().firestore();

      // Una sola contabilización por documento (lib/accounting_refs.js). Se
      // relee el doc ANTES de subir a Storage: rechazar después dejaría el
      // PDF subido y huérfano. Cubre también dos pestañas abiertas sobre el
      // mismo documento, que el botón oculto en pantalla no ve.
      const current = await reference.ref.get();
      const blocked = accountingUploadBlock( current.data()?.accounting );
      if ( blocked ) {
        return reject(
          dispatch({ type: UPLOAD.ERROR, code: 'accounting-exists', message: blocked })
        );
      }

      const name = `Contabilizacion No - ${ generateFileId() }`;
      const ref = firebase().storage().ref().child(`${ reference.data().folder.path.replace('folders', 'documents').replace('files', 'documents') }/${ name }`);
      const accounting = db.doc(`files/${ name }`);

      // Upload a Storage con retry exponencial, igual que `upload`. Sin esto
      // el put salia pelado y un storage/retry-limit-exceeded (ventana de
      // reintentos del SDK agotada por red mala) tumbaba la fila entera.
      // El retry va AQUI y no afuera a proposito: reintenta solo la operacion
      // de Storage que fallo, sin repetir el batch de Firestore ni el
      // markAccounted, y sin dejar objetos huerfanos por cada intento.
      const snapshot = await retryWithBackoff(
        () => ref.put( file ),
        { maxRetries: 2, baseDelayMs: 1000, label: `storage.put accounting ${file.name}` }
      );
      const url = await ref.getDownloadURL();

      const uid = firebase().auth().currentUser.uid;
      const batch = db.batch();

      const log_audit = db.collection('audit').doc();
      const after = {
        folder: reference.ref,
        customer: reference.data().customer,
        date_of_upload: new Date( snapshot.metadata.timeCreated ),
        name: file.name,
        type: snapshot.metadata.contentType,
        url: url
      }

      batch.set(log_audit, { uid, eventType: `${ DOCUMENTS.CREATE.CREATE_DOCUMENTS }_FILES`, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
      batch.set(log_audit.collection('after').doc(), after);
      batch.set(accounting, after);
      // Append (no sobrescribe): el campo admite varias contabilizaciones y
      // los docs legacy con ref suelta quedan convertidos a array aquí.
      // IMPORTANTE: releer el doc — `reference` puede ser el snapshot de
      // cuando se abrió el drawer; hacer append sobre esa lista estancada
      // pisaba las contabilizaciones subidas en la misma sesión.
      const fresh = await reference.ref.get();
      batch.update(reference.ref, { accounting: [ ...toAccountingList( fresh.data()?.accounting ), accounting ] })

      await batch.commit();

      // Marca automatica de contabilizado al subir el soporte.
      //
      // Pedido por el equipo contable (2026-09-08): aplicar el tag a mano
      // documento por documento les quitaba mucho tiempo, sobre todo despues
      // de una contabilizacion masiva. El boton manual de marcar/desmarcar se
      // conserva — este camino solo evita el clic repetitivo.
      //
      // SOLO si el archivo no estaba ya marcado: si alguien lo marco antes, su
      // autoria y su fecha se respetan en vez de pisarse con las de esta subida.
      //
      // Cubre los dos flujos con un solo cambio, porque la contabilizacion
      // masiva reutiliza este mismo thunk (documents/bulk_accounting.js).
      //
      // Best-effort: el soporte contable YA quedo subido y escrito. Si la
      // marca falla (permisos, red), se avisa en consola y la subida se da por
      // buena — el usuario siempre puede marcar con el boton.
      if ( !fresh.data()?.accounted ) {
        try {
          await dispatch( filesActions.markAccounted({
            // El gate real de esta accion son las rules; en la UI el boton de
            // subir contabilizacion ya exige documents.accounting, asi que
            // quien llega aqui tiene el permiso. Mismo criterio que bulk_tree.
            roles: { accounting: true },
            reference: reference.ref
          }));
        } catch ( e ) {
          console.warn('[contabilizado] no se pudo marcar automaticamente:', e?.message || e);
        }
      }

      // El listado pinta el tag "Contabilizado" desde la entrada de files[],
      // que no tiene el campo accounting. Sin este flag el cliente no veria la
      // marca hasta abrir el archivo.
      await denormalizeFileFlagsToParent( reference.ref, firebase );
      return resolve(
        dispatch({  
          type: UPLOAD.UPLOADED,
          data: snapshot
        }
      ));

    } catch ( error ) {
      return reject(
        dispatch({
          type: UPLOAD.ERROR,
          code: error.code,
          message: error.message
        })
      )
    }
  });

}

/**
 * Función que permite subir el archivo XML al storage
 * @param {object} file 
 * @param {object} reference 
 */
export const xml = ( file, reference ) => ( dispatch, state, firebase ) => {

  dispatch({ type: UPLOAD.UPLOADING });

  return new Promise( async ( resolve, reject ) => {
    try {

      const db = firebase().firestore();
      const name = `XML No - ${ generateFileId() }`;
      const ref = firebase().storage().ref().child(`${ reference.data().folder.path.replace('folders', 'documents').replace('files', 'documents') }/${ name }`);
      const xml = db.doc(`files/${ name }`);

      const snapshot = await ref.put( file );
      const url = await ref.getDownloadURL();

      const uid = firebase().auth().currentUser.uid;
      const batch = db.batch();

      const log_audit = db.collection('audit').doc();
      const after = {
        folder: reference.ref,
        customer: reference.data().customer,
        date_of_upload: new Date( snapshot.metadata.timeCreated ),
        name: file.name,
        type: snapshot.metadata.contentType,
        url: url
      }

      batch.set(log_audit, { uid, eventType: `${ DOCUMENTS.CREATE.CREATE_DOCUMENTS }_FILES`, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
      batch.set(log_audit.collection('after').doc(), after);
      batch.set(xml, after);
      batch.update(reference.ref, { xml })
      
      await batch.commit();
      return resolve(
        dispatch({  
          type: UPLOAD.UPLOADED,
          data: snapshot
        }
      ));

    } catch ( error ) {
      return reject(
        dispatch({
          type: UPLOAD.ERROR,
          code: error.code,
          message: error.message
        })
      )
    }
  });

}

/**
 * Función que permite subir el archivo vinculado al storage
 * @param {object} file 
 * @param {object} reference 
 */
export const linking = ( file, reference ) => ( dispatch, state, firebase ) => {

  dispatch({ type: UPLOAD.UPLOADING });

  return new Promise( async ( resolve, reject ) => {
    try {

      const db = firebase().firestore();
      const name = `Vinculo No - ${ generateFileId() }`;
      const ref = firebase().storage().ref().child(`${ reference.data().folder.path.replace('folders', 'documents').replace('files', 'documents') }/${ name }`);
      const linking = db.doc(`files/${ name }`);

      const snapshot = await ref.put( file );
      const url = await ref.getDownloadURL();

      const uid = firebase().auth().currentUser.uid;
      const batch = db.batch();

      const log_audit = db.collection('audit').doc();
      const after = {
        folder: reference.ref,
        customer: reference.data().customer,
        date_of_upload: new Date( snapshot.metadata.timeCreated ),
        name: file.name,
        type: snapshot.metadata.contentType,
        url: url
      }

      batch.set(log_audit, { uid, eventType: `${ DOCUMENTS.CREATE.CREATE_DOCUMENTS }_FILES`, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
      batch.set(log_audit.collection('after').doc(), after);
      batch.set(linking, after);
      batch.update(reference.ref, { links: firebase().firestore.FieldValue.arrayUnion( linking ) })
      
      await batch.commit();
      return resolve(
        dispatch({  
          type: UPLOAD.UPLOADED,
          data: snapshot
        }
      ));

    } catch ( error ) {
      return reject(
        dispatch({
          type: UPLOAD.ERROR,
          code: error.code,
          message: error.message
        })
      )
    }
  });

}